/**
 * Types + fetchery zamawiania uslugi z publicznej strony z oferta pakietow
 * (katalog `c_crm_project_management`).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET  /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/orderForm/{id_package}
 *   - POST /IRESTAPI/v1/C_CRM_PROJECT_MANAGEMENT_CONTROLLER/orderSubmit
 *
 * Oba endpointy sa PUBLICZNE - oferte zamawia takze ktos bez konta. Sesja, gdy
 * istnieje, zmienia tylko ksztalt formularza (dane do potwierdzenia + wybor
 * projektu) i jest doklejana jako Bearer przez proxy w `app/api/iiapi/...`.
 *
 * Zestaw pol jest MINIMALNY i ustala go backend: tyle, ile trzeba do wystawienia
 * dokumentu, a dla goscia dodatkowo do zalozenia konta. Wariant nabywcy (firma /
 * osoba prywatna) rozstrzygaja grupy uzytkownikow z konfiguracji CRM - to one decyduja
 * m.in. o tym, czy formularz pyta o NIP.
 */

/** Pole formularza zamowienia ze stalej listy backendu. */
export type OrderFormField = {
  /**
   * Sekcja formularza: kontakt / rejestracja, dane firmowe (wariant firmowy) albo dane
   * do faktury (wariant prywatny). `company` i `billing` niosa te same kolumny - roznica
   * jest w naglowku, bo zamawiajac jako firma podaje sie dane firmowe.
   */
  section: "contact" | "company" | "billing";
  /** Nazwa kolumny uzytkownika - klucz w `fields` przy zapisie zamowienia. */
  name: string;
  /** Etykieta z tlumaczen strony grup uzytkownikow. */
  label: string;
  /** `true` = grupa oznaczyla pole jako wymagane (stan 2 w konfiguracji). */
  required: boolean;
  /** Typ kontrolki wyliczony przez backend z nazwy pola. */
  type: "text" | "email" | "tel" | "date";
  /**
   * Symbol slownika (`IIAPI_getPublicDictionary`), gdy pole jest wyborem z listy -
   * dzis `countries` dla kraju. NULL = zwykle pole tekstowe. Zapisywana jest wartosc
   * `id` ze slownika (np. `PL`), tak samo jak w edycji danych w panelu klienta.
   */
  dictionary: "countries" | null;
  /** Wartosc z konta zalogowanego klienta; pusty string dla goscia. */
  value: string;
};

/** Wariant konta, w ktorym gosc zaklada konto skladajac zamowienie. */
export type OrderAccountType = {
  key: "company" | "private";
  id_user_group: number;
  fields: OrderFormField[];
};

/** Pakiet w skroconej formie - podsumowanie nad formularzem. */
export type OrderFormPackage = {
  id: number;
  name: string | null;
  price_netto: string;
  price_brutto: string;
  /** Etykieta okresu rozliczeniowego; NULL dla pakietu jednorazowego. */
  period_label: string | null;
  /** Pakiet mozna zamowic jako bezplatny test (usluga startuje bez platnosci). */
  trial_enabled: boolean;
  trial_days: number;
  /** Pakiet za zero zlotych - nie przechodzi przez pro forme. */
  free: boolean;
};

/** Typy dodatkowych pol pakietu - odpowiadaja stalym PACKAGE_FIELD_TYPE_* w module Uslugi. */
export const ORDER_FIELD_TEXT = 1;
export const ORDER_FIELD_TEXTAREA = 2;
export const ORDER_FIELD_DATE = 4;
export const ORDER_FIELD_PRICE = 5;
export const ORDER_FIELD_NUMBER = 6;
export const ORDER_FIELD_DIGIT = 7;
export const ORDER_FIELD_URL = 8;

/** Pole wymagane od klienta przy zamowieniu (definicja pola dodatkowego pakietu). */
export type OrderServiceField = {
  id: number;
  /** Etykieta z definicji pola w pakiecie. */
  name: string;
  /** Typ pola - jedna ze stalych ORDER_FIELD_*; decyduje o kontrolce i walidacji. */
  type: number;
  /** Pole zawsze wymagane: do zamowienia trafiaja wylacznie pola oznaczone tak w pakiecie. */
  required: boolean;
  /** Dolna granica dla pol liczbowych; null = klient moze podac dowolna liczbe. */
  min_value: number | null;
  /** Opis dla klienta - z definicji pola albo domyslny z modulu Uslugi. */
  description: string;
  placeholder: string;
};

export type OrderFormData = {
  package: OrderFormPackage;
  /** Warianty konta wystawione przez administratora; pusta lista = brak konfiguracji grup. */
  account_types: OrderAccountType[];
  /**
   * Pola konta zalogowanego klienta, wypelnione jego danymi - do potwierdzenia albo
   * poprawienia przed wystawieniem dokumentu. Dla goscia pusta lista, bo zestaw pol
   * zalezy dopiero od wybranego wariantu konta (`account_types[].fields`).
   */
  fields: OrderFormField[];
  /**
   * Dodatkowe pola pakietu oznaczone jako WYMAGANE - klient musi je uzupelnic przy zamowieniu
   * (adres strony dla uslug monitorowanych, liczba godzin obslugi w SLA). Pusta lista, gdy
   * pakiet niczego od klienta nie potrzebuje. Wartosci wracaja w mapie `service_fields`.
   */
  service_fields: OrderServiceField[];
  /** Regulamin obowiazujacy dla pakietu; NULL gdy pakiet go nie wymaga. */
  regulamin: { id: number; text: string } | null;
  logged_in: boolean;
  /** Dane zalogowanego klienta do potwierdzenia; NULL dla goscia. */
  user: Record<string, unknown> | null;
  /** Projekty, do ktorych zalogowany klient moze dopiac usluge. */
  projects: { id: number; name: string | null }[];
  /**
   * Czy w instalacji dziala integracja z GUS (jest klucz API). `false` = formularz nie
   * pokazuje przycisku pobrania danych po NIP, bo nie ma czego zawolac.
   */
  gus_available: boolean;
};

/** Wynik wyszukania firmy w rejestrze REGON po numerze NIP. */
export type OrderCompanyLookup = {
  /** `false` = rejestr nie zna firmy o tym NIP; `message` niesie komunikat do pokazania. */
  found: boolean;
  message: string;
  /** Pola formularza gotowe do wstawienia; rejestr moze nie znac wszystkich. */
  fields: Record<string, string>;
};

/** Rezultat zlozenia zamowienia - dwa mozliwe zakonczenia sciezki. */
export type OrderResult =
  | {
      /** Usluga uruchomiona od razu (test albo pakiet bezplatny). */
      status: "created";
      id_project: number;
      id_service: number;
      message: string;
    }
  | {
      /** Wystawiona pro forma - usluga powstanie po zaksiegowaniu wplaty. */
      status: "payment";
      id_facture: number;
      payment_url: string;
      message: string;
    };

export type OrderPayload = {
  id_package: number;
  /** Wymagany tylko dla goscia - dla zalogowanego konto juz istnieje. */
  account_type?: "company" | "private";
  /**
   * Dane z formularza. Dla goscia zakladaja konto, dla zalogowanego aktualizuja jego
   * dane rozliczeniowe przed wystawieniem dokumentu.
   */
  fields?: Record<string, string>;
  regulamin_accept?: boolean;
  /** Wartosci pol dodatkowych pakietu (`service_fields`), kluczem jest ID definicji pola. */
  service_fields?: Record<string, string>;
  trial?: boolean;
  /** Istniejacy projekt zalogowanego klienta; brak = zamowienie zaklada nowy. */
  id_project?: number;
};

/** Blad zamowienia niosacy kod Imperiala - formularz mapuje go na komunikat przy polu. */
export class OrderApiError extends Error {
  readonly code: number;

  constructor(message: string, code: number) {
    super(message);
    this.name = "OrderApiError";
    this.code = code;
  }
}

/**
 * Wspolne odczytanie odpowiedzi proxy - rzuca `OrderApiError` z kodem Imperiala,
 * zeby formularz mogl przypiac komunikat do konkretnego pola zamiast pokazywac
 * ogolny "cos poszlo nie tak".
 */
async function readOrderJson<T>(res: Response, fallbackMessage: string): Promise<T> {
  const body = (await res.json().catch(() => null)) as
    | { ok: true; data: T }
    | { ok: false; error?: string; message?: string; code?: number }
    | null;

  if (!body || !("ok" in body)) {
    throw new OrderApiError(fallbackMessage, 0);
  }
  if (body.ok !== true) {
    throw new OrderApiError(body.message ?? fallbackMessage, body.code ?? 0);
  }
  return body.data;
}

/**
 * Pobiera definicje formularza zamowienia dla pakietu.
 *
 * @param idPackage - ID pakietu z oferty
 * @returns Pakiet, warianty konta z polami, regulamin i kontekst zalogowania
 * @throws OrderApiError gdy response !ok (caller obsluguje przez try/catch).
 */
export async function fetchOrderForm(idPackage: number): Promise<OrderFormData> {
  const res = await fetch(
    `/api/iiapi/c-crm-project-management/order-form?id_package=${encodeURIComponent(String(idPackage))}`,
    { cache: "no-store" },
  );
  return readOrderJson<OrderFormData>(res, "Nie udało się wczytać formularza zamówienia.");
}

/**
 * Pobiera dane firmy z rejestru REGON (GUS) po numerze NIP.
 *
 * @param nip - Numer NIP wpisany w formularzu
 * @returns `found: false` gdy rejestr nie zna firmy - to wynik wyszukiwania, nie blad
 * @throws OrderApiError gdy response !ok (caller obsluguje przez try/catch).
 */
export async function lookupOrderCompany(nip: string): Promise<OrderCompanyLookup> {
  const res = await fetch("/api/iiapi/c-crm-project-management/order-company", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nip }),
    cache: "no-store",
  });
  return readOrderJson<OrderCompanyLookup>(res, "Nie udało się pobrać danych firmy.");
}

/**
 * Sklada zamowienie uslugi.
 *
 * @param payload - Wybrany pakiet, wariant konta z danymi, akceptacja regulaminu
 * @returns `status: 'created'` (usluga juz dziala) albo `status: 'payment'` (pro forma)
 * @throws OrderApiError gdy response !ok (caller obsluguje przez try/catch).
 */
export async function submitOrder(payload: OrderPayload): Promise<OrderResult> {
  const res = await fetch("/api/iiapi/c-crm-project-management/order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  return readOrderJson<OrderResult>(res, "Nie udało się złożyć zamówienia.");
}
